import { useMemo, type ReactNode } from 'react';
import { PERMISSIONS, useGuestKeySummary, usePermissions } from '@bms/common';
import type { GuestKeySummaryRoom } from '@bms/common';

/** Active guest keys per room code. */
export type GuestKeyCounts = ReadonlyMap<string, number>;

/** The summary's rows as the per-room count map the desk reads. */
export function guestKeyCounts(rooms: readonly GuestKeySummaryRoom[]): GuestKeyCounts {
  return new Map(rooms.map((room) => [room.roomCode, room.active]));
}

function useGuestKeyCounts(): GuestKeyCounts | undefined {
  const { data } = useGuestKeySummary();
  return useMemo(() => (data ? guestKeyCounts(data) : undefined), [data]);
}

function Counted({ children }: { children: (counts: GuestKeyCounts | undefined) => ReactNode }) {
  return <>{children(useGuestKeyCounts())}</>;
}

/**
 * Hands its children the active guest keys per room, from Edge's per-room
 * count (`GET /access/cards/guest-key-summary`) — every room, however many
 * cards the site holds. The read runs only for a holder
 * of `cards:read` (the route refuses everyone else); without it the children
 * get `undefined` and show no key counts.
 */
export function WithGuestKeyCounts({ children }: { children: (counts: GuestKeyCounts | undefined) => ReactNode }) {
  const permissions = usePermissions();
  return permissions.has(PERMISSIONS.CARDS_READ)
    ? <Counted>{children}</Counted>
    : <>{children(undefined)}</>;
}
